/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useMemo, useState } from "react";

import {
  Badge,
  Button,
  FontIcon,
  TableColumnDefinition,
  TableRowDefinition,
  TableWithDefaultFilters,
  Typography,
} from "@ezmid/components-library/";
import {
  faInfoCircle,
  faSortNumericDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
  EntityLessonContentTranslation,
  EntityLessonContent,
  EntityMedia,
} from "../../../api/lesson.generated";
import { useDeleteLessonContentByIdMutation } from "../../../api/lessons-update-content.generated";
import { DeleteConfirmationModal } from "../../../components/modals/DeleteConfirmationModal";
import { truncateWithEllipsis } from "../../../utils/stringUtils";
import { AddLessonContentModal } from "../molecules/AddLessonContentModal";
import { UpdateLessonContentModal } from "../molecules/UpdateLessonContentModal";

type Props = {
  lessonContents: Array<EntityLessonContent>;
  refetch: () => void;
};

export type LessonContent = {
  itemOrder: string;
  mediaID: string;
  lessonContentType?: string;
  translations?: Array<EntityLessonContentTranslation>;
  contentID: string;
  media?: EntityMedia;
  status?: string;
};

type TableData = {
  id: string;
  name: string;
  order: number;
  lessonContentType?: string;
  mediaID: string;
  translations?: Array<EntityLessonContentTranslation>;
  media?: EntityMedia;
  status?: string;
};

export function LessonContentCard({ lessonContents, refetch }: Props) {
  const { t, i18n } = useTranslation();
  const [contentDetail, setContentDetail] = useState<LessonContent>({
    itemOrder: "",
    mediaID: "",
    lessonContentType: "lct-theory",
    contentID: "",
    translations: [],
    status: "lcs-draft",
  });
  const [isUpdateContentModalOpen, setIsUpdateContentModalOpen] =
    useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [deleteLessonContent, { isLoading: isDeleting }] =
    useDeleteLessonContentByIdMutation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const handleDeleteClick = useCallback(
    (contentId: string, contentName: string) => {
      setContentToDelete({ id: contentId, name: contentName });
      setIsDeleteModalOpen(true);
    },
    []
  );

  const handleConfirmDelete = async () => {
    if (!contentToDelete) return;

    try {
      await deleteLessonContent({ id: contentToDelete.id }).unwrap();
      toast.success(t("app.lessonContent.delete.success"));
      setIsDeleteModalOpen(false);
      setContentToDelete(null);
      refetch();
    } catch (error) {
      toast.error(t("app.lessonContent.delete.failed"));
    }
  };

  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setContentToDelete(null);
    }
  };

  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.name"),
        id: "name",
        accessor: "name",
        Cell: ({ row }: TableRowDefinition<TableData>) => {
          return (
            <Typography variant="body1">
              {truncateWithEllipsis(row?.original?.name || "", 35)}
            </Typography>
          );
        },
        width: "280px",
      },
      {
        Header: t("table.header.contentType"),
        id: "lessonContentType",
        accessor: "lessonContentType",
        Cell: ({ row }: TableRowDefinition<TableData>) => {
          return (
            <Typography variant="body1">
              {row?.original?.lessonContentType || ""}
            </Typography>
          );
        },
        width: "150px",
        maxWidth: "150px",
      },
      {
        Header: t("table.header.order"),
        Cell: ({ row }: TableRowDefinition<TableData>) => (
          <Badge
            variant="pill"
            leftIcon={
              <FontIcon
                marginTop={3}
                customIcon={
                  <FontAwesomeIcon size="lg" icon={faSortNumericDown} />
                }
              />
            }
          >
            {row?.original?.order ?? ""}
          </Badge>
        ),
        id: "order",
        accessor: "order",
        width: "50px",
        maxWidth: "50px",
      },

      {
        Header: "",
        id: "actions",
        accessor: "id",
        Cell: ({ row }: TableRowDefinition<TableData>) => {
          return (
            <div className="flex justify-end pr-2">
              <Button
                variant="danger"
                size="sm"
                style={{ borderRadius: "0.2rem" }}
                onClick={() => {
                  handleDeleteClick(row?.original.id, row?.original.name);
                }}
              >
                {t("button.delete")}
              </Button>
            </div>
          );
        },
        width: "150px",
      },
    ],
    [t, handleDeleteClick]
  );

  const memoizedData: Array<TableData> = useMemo(() => {
    const langCode = i18n.language.split("-")[0];

    return lessonContents
      .filter(
        (content): content is EntityLessonContent => content !== undefined
      )
      .map((content) => {
        const translatedName = content?.translations?.find(
          (translation) => translation?.language === langCode
        )?.name;

        return {
          id: content.id || "",
          name: translatedName || "",
          order: content.order ?? 0,
          mediaType: content.media?.type || "",
          lessonContentType: content.lessonContentType,
          mediaID: content.media?.id || "",
          translations: content.translations,
          media: content.media,
          status: content.status,
        };
      });
  }, [lessonContents, i18n.language]);

  return (
    <>
      <div>
        <TableWithDefaultFilters
          tableData={memoizedData}
          tableColumns={columns}
          showPagination={false}
          showToolbar={false}
          headerIcon={
            <FontIcon
              marginTop={4}
              customIcon={<FontAwesomeIcon icon={faInfoCircle} />}
            />
          }
          headerText={t("course.lesson.content")}
          onRowSelect={(row) => {
            setIsUpdateContentModalOpen(true);
            setContentDetail({
              mediaID: row?.original?.mediaID || "",
              itemOrder: row?.original?.order?.toString() || "",
              lessonContentType: row?.original?.lessonContentType,
              translations: row?.original?.translations,
              contentID: row?.original?.id || "",
              media: row?.original?.media,
              status: row?.original?.status,
            });
          }}
        />
      </div>
      <AddLessonContentModal
        onModalClose={() => {
          setIsModalOpen(false);
        }}
        refetch={refetch}
        isModalOpen={isModalOpen}
      />
      <UpdateLessonContentModal
        onModalClose={() => {
          setIsUpdateContentModalOpen(false);
        }}
        contentDetail={contentDetail}
        refetch={refetch}
        isModalOpen={isUpdateContentModalOpen}
      />
      <DeleteConfirmationModal
        isModalOpen={isDeleteModalOpen}
        onModalClose={handleCloseDeleteModal}
        onConfirmDelete={handleConfirmDelete}
        itemName={contentToDelete?.name || ""}
        isDeleting={isDeleting}
        titleKey="modal.deleteLessonContent.heading"
        confirmMessageKey="app.lessonContent.delete.confirm"
      />
    </>
  );
}
